import { z } from 'zod'

/**
 * Money on the cabinet: integer hundredths of a credit (`CREDIT_UNITS` = 100
 * stored units to the credit, so 0.50 credits are 50 units). Floats never
 * touch money: a decimal text goes in through `creditsFromDecimal` and comes
 * out through `formatCredits`, both on integers. The brand
 * marks an amount that was checked, so a plain number (a UI value, a
 * product of anything) cannot reach a money field by accident. Only
 * producers check: `credits()` here, the contract decoders and
 * `creditsSchema` for what a server sends. `Credits` widens to `number`, so
 * formatting and display code takes it as is. Engine amounts (`SpinResult`)
 * stay plain numbers.
 */

declare const creditsBrand: unique symbol

/** Integer hundredths of a credit (50 = 0.50 credits). Never negative. */
export type Credits = number & { readonly [creditsBrand]: true }

/** A non-negative safe integer: larger values lose precision as a JSON number. */
export function isCredits(value: unknown): value is Credits {
  return Number.isSafeInteger(value) && (value as number) >= 0
}

/** Checks `value` and brands it. @throws RangeError when it is not credits. */
export function credits(value: number): Credits {
  if (!isCredits(value)) {
    throw new RangeError(
      `credits must be a non-negative safe integer, got ${value}`,
    )
  }
  return value
}

export const ZERO_CREDITS = credits(0)

/** Stored units to the credit: amounts are kept in hundredths of a credit. */
export const CREDIT_UNITS = 100

const DECIMAL_DIGITS = 2
const DECIMAL_CREDITS = /^(\d+)(?:\.(\d{1,2}))?$/

/**
 * Reads a decimal text of credits (`"0.50"`, `"1"`, `"100.00"`) as stored
 * units, with no float math: at most two decimals, no sign, no exponent.
 *
 * @throws RangeError when `text` is no such amount, has a third decimal
 *         (`"0.505"`), or is no safe integer in units.
 */
export function creditsFromDecimal(text: string): Credits {
  const match = DECIMAL_CREDITS.exec(text)
  if (match === null) {
    throw new RangeError(
      `credits must be a decimal with at most ${DECIMAL_DIGITS} decimals, got ${JSON.stringify(text)}`,
    )
  }
  const [, whole, fraction = ''] = match
  const units =
    BigInt(whole) * BigInt(CREDIT_UNITS) +
    BigInt(fraction.padEnd(DECIMAL_DIGITS, '0'))
  if (units > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError(`credits are too large, got ${JSON.stringify(text)}`)
  }
  return credits(Number(units))
}

/** `credits` as a text with two decimals (`"0.50"`), with no float math. */
export function formatCredits(amount: Credits): string {
  const whole = Math.floor(amount / CREDIT_UNITS)
  const fraction = amount - whole * CREDIT_UNITS
  return `${whole}.${String(fraction).padStart(DECIMAL_DIGITS, '0')}`
}

/** @throws RangeError when the sum is no longer a safe integer. */
export function addCredits(...values: readonly Credits[]): Credits {
  return credits(values.reduce((sum, value) => sum + value, 0))
}

/**
 * `amount − subtrahend`. An over-debit is a bug, so a negative result throws
 * instead of being clamped to 0.
 *
 * @throws RangeError when `subtrahend` exceeds `amount`.
 */
export function subCredits(amount: Credits, subtrahend: Credits): Credits {
  return credits(amount - subtrahend)
}

/**
 * `amount × factor`, for counts such as played lines or the gamble's ×2.
 *
 * @throws RangeError when `factor` is not a non-negative safe integer, or
 *         the product is not a safe integer.
 */
export function mulCredits(amount: Credits, factor: number): Credits {
  if (!Number.isSafeInteger(factor) || factor < 0) {
    throw new RangeError(
      `a credits factor must be a non-negative safe integer, got ${factor}`,
    )
  }
  return credits(amount * factor)
}

/** The stake a spin is played at: `betPerLine` on each of the first `lines` paylines. */
export interface Stake {
  betPerLine: Credits
  lines: number
}

/** What a paid spin at `stake` costs. @throws RangeError as `mulCredits`. */
export function stakeTotal(stake: Stake): Credits {
  return mulCredits(stake.betPerLine, stake.lines)
}

// -----------------------------------------------------------------------------
// Schemas
// -----------------------------------------------------------------------------

const CREDITS = 'credits (a non-negative safe integer)'

/**
 * Credits on the wire. A JSON Schema of it is a non-negative integer (the
 * brand check is a `pipe` after it, so `z.toJSONSchema` with `io: 'input'`
 * still works).
 */
export const creditsSchema = z
  .int({ error: CREDITS })
  .min(0, { error: CREDITS })
  .pipe(z.custom<Credits>(isCredits, { error: CREDITS }))

/** Credits above 0: a pending win, a ladder total, a bet per line. */
export const positiveCreditsSchema = z
  .int({ error: CREDITS })
  .min(1, { error: 'credits above 0' })
  .pipe(z.custom<Credits>(isCredits, { error: CREDITS }))

/** A positive bet on at least one line, whose total is still exact. */
export const stakeSchema = z
  .object({
    betPerLine: positiveCreditsSchema,
    lines: z.int().min(1),
  })
  .refine(({ betPerLine, lines }) => Number.isSafeInteger(betPerLine * lines), {
    error: 'a stake whose total is a safe integer',
  }) satisfies z.ZodType<Stake>
